import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import DecisionEvidencePanel from '../coach/DecisionEvidencePanel'
import NutritionAdherenceCard from '../coach/NutritionAdherenceCard'
import { KILO } from '../../constants/KILO_THEME'
import type { AppliedEvidenceRule } from '../../lib/dailyOverrideTypes'
import type { DailyNutritionSummary } from '../../lib/mealLogCore'
import { buildWellnessProtocolPreview } from '../../lib/wellnessProtocolPreview'
import type { WellnessPillars } from '../../lib/submitMorningWellness'

type NonWearableBriefingProps = {
  title: string
  nutritionSummary: DailyNutritionSummary | null
  nutritionCoachNote?: string | null
  nutritionFirstDay?: boolean
  evidenceApplied: AppliedEvidenceRule[]
  pendingWellness: WellnessPillars | null
  awaitingFinish: boolean
  onFinish: () => void
  dispatching?: boolean
  embedded?: boolean
}

const NonWearableBriefing = ({
  title,
  nutritionSummary,
  nutritionCoachNote,
  nutritionFirstDay = false,
  evidenceApplied,
  pendingWellness,
  awaitingFinish,
  onFinish,
  dispatching = false,
  embedded = false,
}: NonWearableBriefingProps) => {
  const { t } = useTranslation()

  const previewEvidence = useMemo(() => {
    if (evidenceApplied.length > 0) return evidenceApplied
    if (!pendingWellness) return []
    return buildWellnessProtocolPreview(pendingWellness)
  }, [evidenceApplied, pendingWellness])

  const shell = embedded ? KILO.embeddedSection : 'rounded-2xl border border-kilo-border bg-kilo-card p-5'

  return (
    <div className={shell}>
      <p className={KILO.meta}>{title}</p>
      <p className={`mt-2 text-sm leading-relaxed ${KILO.text.muted}`}>
        {t('coach.briefing.nonWearableIntro')}
      </p>

      <div className="mt-5 space-y-1">
        <p className={KILO.meta}>{t('coach.briefing.yesterdayAudit')}</p>
        <NutritionAdherenceCard
          todaySummary={null}
          yesterdaySummary={nutritionSummary}
          weekTargetKcal={nutritionSummary?.planned.calories_kcal ?? 0}
          weekActualKcal={nutritionSummary?.actual.calories_kcal ?? 0}
          coachNote={nutritionCoachNote}
          isFirstDay={nutritionFirstDay}
          primaryFocus="yesterday"
          embedded
        />
      </div>

      {previewEvidence.length > 0 ? (
        <div className="mt-4">
          <DecisionEvidencePanel evidence={previewEvidence} trackUpdates />
        </div>
      ) : null}

      {awaitingFinish ? (
        <button
          type="button"
          disabled={dispatching}
          onClick={onFinish}
          className="mt-6 flex h-12 w-full items-center justify-center rounded-xl bg-kilo-accent text-sm font-bold text-black disabled:opacity-50"
        >
          {dispatching ? t('coach.briefing.finalizing') : t('coach.briefing.finishAndFinalize')}
        </button>
      ) : null}
    </div>
  )
}

export default NonWearableBriefing
